import React from 'react';
import { mkNode, mkEdge, FlowCanvas } from './flowParts';

// Colors by role, shared across both forecast diagrams.
const C = {
  source: '#4aa3ff',   // external data providers
  check: '#9aa6b5',    // ground-truth validation
  compute: '#e6a84a',  // backend compute
  store: '#43d17f',    // persisted state
  model: '#14e0c8',    // client-side physics
  ui: '#b89cff',       // user-facing surfaces
};

// 1 · Where the numbers come from: providers → cache worker → page (+ live fallback).
export function ForecastDataFlow() {
  const nodes = [
    mkNode('sg', 0, 0, 'Stormglass (sg blend)', 'primary swell + wind · 0–10 d', C.source),
    mkNode('gfs', 0, 92, 'NCEP GFS-Wave', 'swell tail 10–16 d + tertiary', C.source),
    mkNode('om', 0, 184, 'Open-Meteo marine', 'tide (sea level) + sun + live', C.source),
    mkNode('buoy', 0, 276, 'NOAA NDBC buoys', 'ground-truth spot checks', C.check),
    mkNode('worker', 280, 92, 'Cache worker', 'blends sources · every 6 h', C.compute),
    mkNode('cache', 560, 92, 'forecastCache/{spot}', '16-day hourly series', C.store),
    mkNode('live', 560, 220, 'Live OM fallback', 'in-browser, keyless', C.compute),
    mkNode('page', 840, 150, 'Forecast page', 'cards · meteogram · Gantt', C.ui),
  ];
  const edges = [
    mkEdge('sg', 'worker', 'swell + wind', { color: C.source }),
    mkEdge('gfs', 'worker', 'tail + 3rd swell', { color: C.source }),
    mkEdge('om', 'worker', 'tide + sun', { color: C.source }),
    mkEdge('buoy', 'worker', 'validate', { color: C.check }),
    mkEdge('worker', 'cache', 'write 6 h', { color: C.store, animated: true }),
    mkEdge('cache', 'page', 'read', { color: C.ui }),
    mkEdge('om', 'live', 'cache miss', { color: C.compute }),
    mkEdge('live', 'page', 'fallback', { color: C.ui }),
  ];
  const legend = [
    ['Data provider', C.source],
    ['Ground truth', C.check],
    ['Backend compute', C.compute],
    ['Cached state', C.store],
    ['UI', C.ui],
  ];
  return <FlowCanvas nodes={nodes} edges={edges} legend={legend} height={420} />;
}

// 2 · From a raw hour to forecast surf: partitions → trains → combine → calibrate
// → {consistency, strike, display}.
export function SurfModelFlow() {
  const nodes = [
    mkNode('hour', 0, 150, 'Hourly record', 'P1/P2/P3 swell + wind sea + tide', C.store),
    mkNode('trains', 250, 150, 'swellTrains()', 'rank by power H²·T · peak period', C.model),
    mkNode('combine', 500, 150, 'combinedSurf()', 'quadrature + directional align', C.model),
    mkNode('calib', 760, 70, 'Spot calibration', 'face = k·Hsᵉ·(T/Tref)ᵖ', C.model),
    mkNode('cons', 760, 170, 'waveConsistency()', 'set-frequency index 0–100', C.model),
    mkNode('strike', 760, 270, 'Strike scoring', 'thresholds + wind + tide', C.compute),
    mkNode('ui', 1040, 170, 'Display', 'face @ blended Tp + raw P1/P2/P3', C.ui),
  ];
  const edges = [
    mkEdge('hour', 'trains', '', { color: C.model }),
    mkEdge('trains', 'combine', 'blend', { color: C.model }),
    mkEdge('combine', 'calib', 'eff. swell', { color: C.model }),
    mkEdge('combine', 'cons', 'spectrum', { color: C.model }),
    mkEdge('calib', 'strike', 'face', { color: C.compute }),
    mkEdge('calib', 'ui', '', { color: C.ui }),
    mkEdge('cons', 'ui', '', { color: C.ui }),
    mkEdge('strike', 'ui', 'windows', { color: C.ui }),
  ];
  const legend = [
    ['Cached state', C.store],
    ['Client physics', C.model],
    ['Scoring', C.compute],
    ['UI', C.ui],
  ];
  return <FlowCanvas nodes={nodes} edges={edges} legend={legend} height={440} />;
}
